import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import { DownloadTableExcel } from 'react-export-table-to-excel';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import LoadingSpiner from '../components/LoadingSpiner';
import {
  capitalizeWords,
  formatPhoneNumber,
  formatPrice,
} from '../components/capitalizeFunction';
import { connectedUserBoutique } from '../Authentication/userInfos';
import ListPagination from '../components/ListPagination';
import { usePagignationPaiements } from '../../Api/queriesPaiement';
import { useBilanSummary } from '../../Api/queriesBilan';

/**
 * Page Bilans — optimisée pour la prod (VPS / RAM) :
 * - Avant : useAllPaiements + useAllCommandes + useAllDepenses puis filtres JS sur tout le jeu de données.
 * - Maintenant :
 *   - GET /api/bilan/summary : agrégats MongoDB (KPI) selon boutique + plage de dates.
 *   - GET /api/paiements/getPagignationPaiements : tableau paginé, même filtres + enrichItems pour les noms produits.
 * Les libellés affichés et la logique métier (dates : les deux champs requis pour filtrer, comme avant) restent alignés.
 */
export default function Bilans() {
  const [page, setPage] = useState(1);
  const limit = 35;
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  const tableRef = useRef(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  /** Même règle que l’ancien isBetweenDates : le filtre par date ne s’applique que si début ET fin sont renseignés. */
  const rangeStart = startDate && endDate ? startDate : null;
  const rangeEnd = startDate && endDate ? endDate : null;

  useEffect(() => {
    setPage(1);
  }, [selectedBoutique, rangeStart, rangeEnd]);

  const {
    data: bilanSummary,
    isLoading: bilanLoading,
    error: bilanError,
  } = useBilanSummary(selectedBoutique, rangeStart, rangeEnd);

  const {
    data: paiementsData,
    isLoading: paiementsLoading,
    error: paiementsError,
  } = usePagignationPaiements(
    page,
    limit,
    '',
    selectedBoutique,
    null,
    null,
    rangeStart,
    rangeEnd,
    true
  );

  const isLoading = bilanLoading || paiementsLoading;
  const error = bilanError || paiementsError;

  const filterPaiement = useMemo(
    () => paiementsData?.results?.data || [],
    [paiementsData]
  );

  const totalCommandeNumber = bilanSummary?.commandeCount ?? 0;
  const sumTotalAmount = bilanSummary?.chiffreAffaire ?? 0;
  const sumTotalPaye = bilanSummary?.revenuObtenu ?? 0;
  const sumTotalDepense = bilanSummary?.totalDepenses ?? 0;
  const totalAchat = bilanSummary?.totalAchat ?? 0;
  const benefice = bilanSummary?.benefice ?? 0;
  /** Même chose que l’ancien (Σ commande − Σ payé) ; calculé côté serveur dans summary. */
  const reliquatAffiche = bilanSummary?.reliquatTotal ?? 0;

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <Breadcrumbs title='Rapport' breadcrumbItem='Bilans' />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id='bilanssList'>
                    <Row className='g-4 mb-3 '>
                      <div className='d-flex justify-content-around align-items-center gap-2'>
                        <h3>Bilans de Boutique</h3>
                        <div className='d-flex gap-1'>
                          <DownloadTableExcel
                            filename={
                              'bilans de semaine boutique ' +
                                selectedBoutique ?? '1 et 2'
                            }
                            sheet={
                              'bilans de semaine boutique ' +
                                selectedBoutique ?? '1 et 2'
                            }
                            currentTableRef={tableRef.current}
                          >
                            <Button color='success'>
                              Télécharger en Excel
                            </Button>
                          </DownloadTableExcel>
                        </div>
                      </div>

                      <div className='d-flex justify-content-between flex-wrap align-items-center gap-3'>
                        <div
                          md='12'
                          className='d-flex flex-column justify-content-around mt-4 flex-wrap'
                        >
                          <h6 className=''>
                            Commande Entregistrées:{' '}
                            <span className='text-info'>
                              {formatPrice(totalCommandeNumber || 0)}
                            </span>
                          </h6>
                          <h6 className=''>
                            Chiffre d'Affaire:{' '}
                            <span className='text-info'>
                              {formatPrice(sumTotalAmount || 0)} F{' '}
                            </span>
                          </h6>
                          <h6 className=''>
                            Revenu Obtenu:{' '}
                            <span className='text-success'>
                              {formatPrice(sumTotalPaye || 0)} F{' '}
                            </span>
                          </h6>
                          <h6 className=''>
                            Total Achats :{' '}
                            <span className='text-success'>
                              {formatPrice(totalAchat || 0)} F{' '}
                            </span>
                          </h6>
                          <h6 className=''>
                            Réliquat:{' '}
                            <span className='text-danger'>
                              {formatPrice(reliquatAffiche || 0)} F{' '}
                            </span>
                          </h6>
                          <h6 className=''>
                            Depenses:{' '}
                            <span className='text-danger'>
                              {formatPrice(sumTotalDepense || 0)} F{' '}
                            </span>
                          </h6>
                          <h6 className=''>
                            Benefice:{' '}
                            <span
                              className={`${
                                benefice > 0 ? 'text-primary' : 'text-danger'
                              }`}
                            >
                              {formatPrice(benefice || 0)} F{' '}
                            </span>
                          </h6>
                        </div>

                        <div className='d-flex flex-column gap-3'>
                          {startDate != null && endDate != null && (
                            <Button
                              color='danger'
                              onClick={() => {
                                setSelectedBoutique(null);
                                setStartDate(null);
                                setEndDate(null);
                              }}
                            >
                              Effacer le Filtre
                            </Button>
                          )}
                          <div>
                            <h6>Boutique </h6>
                            <select
                              value={selectedBoutique ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setSelectedBoutique(
                                  v === '' ? null : Number(v)
                                );
                              }}
                              className='form-select border border-dark rounded '
                              style={{ cursor: 'pointer' }}
                            >
                              <option value=''>Toutes</option>
                              <option value={connectedUserBoutique ?? 0}>
                                {connectedUserBoutique ?? 0} - Ma Boutique
                              </option>
                              {connectedUserBoutique === 1 ? (
                                <option value='2'>Boutique - 2</option>
                              ) : connectedUserBoutique === 2 ? (
                                <option value='1'>Boutique - 1</option>
                              ) : (
                                <optgroup label='autres'>
                                  <option value='1'>Boutique - 1</option>
                                  <option value='2'>Boutique - 2</option>
                                </optgroup>
                              )}
                            </select>
                          </div>

                          <div md='4'>
                            <h6>Date de début</h6>
                            <input
                              name='startDate'
                              onChange={(e) => setStartDate(e.target.value)}
                              value={startDate ?? ''}
                              placeholder='Entrez la date de début'
                              type='date'
                              className='form-control p-2 border-1 border-dark'
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                          <div md='4'>
                            <h6>Date de Fin</h6>
                            <input
                              name='endDate'
                              onChange={(e) => setEndDate(e.target.value)}
                              value={endDate ?? ''}
                              placeholder='Entrez la date de Fin'
                              type='date'
                              className='form-control p-2 border-1 border-dark'
                              max={new Date().toISOString().split('T')[0]}
                              min={startDate ?? undefined}
                            />
                          </div>
                        </div>
                      </div>
                    </Row>
                    <ListPagination
                      page={paiementsData?.results?.page ?? page}
                      totalPages={paiementsData?.results?.totalPages ?? 1}
                      onPageChange={setPage}
                      className='mt-2 mb-2'
                    />
                    {error && (
                      <div className='text-danger text-center'>
                        Erreur de chargement des données
                      </div>
                    )}
                    {isLoading && <LoadingSpiner />}

                    <div className='table-responsive table-card mt-3 mb-1'>
                      {filterPaiement?.length === 0 && !isLoading && (
                        <div className='text-center text-mutate'>
                          Aucun paiement trouver !
                        </div>
                      )}
                      <table
                        className='table align-middle table-nowrap table-hover'
                        id='paiementTable'
                        ref={tableRef}
                      >
                        <thead className='table-light'>
                          <tr className='text-center'>
                            <th
                              style={{ width: '50px' }}
                              data-sort='paiementDate'
                            >
                              Date de Commande
                            </th>
                            <th>Articles Commandés</th>
                            <th>Quantité</th>
                            <th>Prix </th>
                            <th data-sort='client'>Client</th>

                            <th data-sort='phoneNumber'>Téléphone</th>
                            <th data-sort='adresse'>Adresse de Livraison</th>

                            <th data-sort='totaAmount'>Montant de Commande</th>
                            <th data-sort='motif'>Réduction</th>
                            <th className='sort' data-sort='totaPayer'>
                              Total Payé
                            </th>
                            <th className='sort' data-sort='reliqua'>
                              Reliquat
                            </th>

                            <th data-sort='methode'>Methode de Paiement</th>
                          </tr>
                        </thead>

                        <tbody className='list form-check-all text-center'>
                          {filterPaiement?.length > 0 &&
                            filterPaiement?.map((paiement) => {
                              return (
                                <tr key={paiement?._id}>
                                  <th scope='row'>
                                    {new Date(
                                      paiement?.commande?.commandeDate
                                    ).toLocaleDateString()}
                                  </th>
                                  <td
                                    className=' text-start'
                                    style={{ width: '400px' }}
                                  >
                                    {paiement?.commande?.items?.map(
                                      (it, index) => (
                                        <p key={index} className='d-block'>
                                          {it?.produit?.name}
                                        </p>
                                      )
                                    )}
                                  </td>
                                  <td>
                                    {paiement?.commande?.items?.map(
                                      (it, index) => (
                                        <p key={index} className='d-block'>
                                          {formatPrice(it?.quantity)}
                                        </p>
                                      )
                                    )}
                                  </td>
                                  <td>
                                    {paiement?.commande?.items?.map(
                                      (it, index) => (
                                        <p key={index} className='d-block'>
                                          {formatPrice(
                                            it?.customerPrice * it?.quantity
                                          )}
                                          {' F '}
                                        </p>
                                      )
                                    )}
                                  </td>

                                  <td>
                                    {capitalizeWords(
                                      paiement?.commande?.fullName
                                    )}
                                  </td>
                                  <td>
                                    {formatPhoneNumber(
                                      paiement?.commande?.phoneNumber
                                    ) || '----'}
                                  </td>
                                  <td>
                                    {capitalizeWords(
                                      paiement?.commande?.adresse
                                    )}
                                  </td>

                                  <td>
                                    {formatPrice(paiement?.totalAmount)}
                                    {' F '}
                                  </td>
                                  <td className='text-warning'>
                                    {formatPrice(paiement?.reduction)} F
                                  </td>
                                  <td>
                                    {formatPrice(paiement?.totalPaye)}
                                    {' F '}
                                  </td>
                                  <td>
                                    {paiement?.totalAmount -
                                      paiement?.totalPaye >
                                    0 ? (
                                      <span className='text-danger'>
                                        {' '}
                                        {formatPrice(
                                          paiement?.totalAmount -
                                            paiement?.totalPaye
                                        )}
                                        {' F '}
                                      </span>
                                    ) : (
                                      <span>
                                        {' '}
                                        {formatPrice(
                                          paiement?.totalAmount -
                                            paiement?.totalPaye
                                        )}
                                        {' F '}
                                      </span>
                                    )}
                                  </td>

                                  <td className='text-warning'>
                                    {capitalizeWords(paiement?.methode)}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
